import React from 'react';
import ReactMarkdown from 'react-markdown';
import './MessageRenderer.scss';

/**
 * MessageRenderer - Enhanced message display with support for:
 * - Markdown formatting
 * - Code blocks with syntax highlighting
 * - Structured data (JSON, milestones, etc.)
 * - Lists and tables
 * - Interactive buttons
 * - Interactive template blanks
 */
export const MessageRenderer = ({
  content,
  structuredData = null,
  buttons = null,
  onButtonClick = null,
  onTemplateClick = null,
}) => {
  // Check if content contains structured data patterns
  const hasStructuredData = structuredData || detectStructuredData(content);

  if (hasStructuredData && structuredData) {
    return (
      <div className='message-renderer'>
        {/* Render conversational content */}
        {content && (
          <div className='message-text'>
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        )}

        {/* Render structured data */}
        {structuredData.milestones && structuredData.milestones.length > 0 && (
          <MilestonesDisplay milestones={structuredData.milestones} />
        )}

        {structuredData.skills && structuredData.skills.length > 0 && (
          <SkillsDisplay skills={structuredData.skills} />
        )}

        {structuredData.budget && (
          <BudgetDisplay budget={structuredData.budget} />
        )}
      </div>
    );
  }

  // Default: render as markdown with optional buttons
  // Check if content has template blanks (_____)
  const hasTemplateBlanks = content && content.includes('_____');

  return (
    <div className='message-renderer'>
      {hasTemplateBlanks && onTemplateClick ? (
        <TemplateContent content={content} onTemplateClick={onTemplateClick} />
      ) : (
        <ReactMarkdown>{content}</ReactMarkdown>
      )}
      {buttons && buttons.length > 0 && (
        <ButtonsDisplay buttons={buttons} onButtonClick={onButtonClick} />
      )}
    </div>
  );
};

/**
 * Detect if content contains structured data patterns
 */
function detectStructuredData(content) {
  if (!content) return false;

  // Check for common structured data patterns
  const patterns = [
    /\d+\.\s+\*\*.*\*\*:/g, // Numbered lists with bold headers
    /```json/g, // JSON code blocks
    /\|\s+.*\s+\|/g, // Tables
  ];

  return patterns.some((pattern) => pattern.test(content));
}

/**
 * Display milestones in a structured card format
 */
const MilestonesDisplay = ({ milestones }) => {
  const totalAmount = milestones.reduce((sum, m) => sum + (m.amount || 0), 0);
  const totalDuration = milestones.reduce(
    (sum, m) => sum + (m.duration_days || 0),
    0
  );

  return (
    <div className='structured-data milestones-display'>
      <div className='structured-header'>
        <h4>📋 Project Milestones</h4>
        <div className='structured-meta'>
          <span className='meta-item'>
            💰 Total: ${totalAmount.toLocaleString()}
          </span>
          <span className='meta-item'>⏱️ {totalDuration} days</span>
        </div>
      </div>

      <div className='milestones-list'>
        {milestones.map((milestone, index) => (
          <div key={index} className='milestone-card'>
            <div className='milestone-header'>
              <span className='milestone-order'>Phase {milestone.order}</span>
              <h5 className='milestone-title'>{milestone.title}</h5>
            </div>

            <p className='milestone-description'>{milestone.description}</p>

            <div className='milestone-footer'>
              <div className='milestone-stat'>
                <span className='stat-label'>Budget</span>
                <span className='stat-value'>
                  ${milestone.amount?.toLocaleString() || 0}
                </span>
              </div>
              <div className='milestone-stat'>
                <span className='stat-label'>Duration</span>
                <span className='stat-value'>
                  {milestone.duration_days || 0} days
                </span>
              </div>
              {milestone.confidence && (
                <div className='milestone-stat'>
                  <span className='stat-label'>Confidence</span>
                  <span className='stat-value'>
                    {(milestone.confidence * 100).toFixed(0)}%
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Display skills as tags
 */
const SkillsDisplay = ({ skills }) => {
  return (
    <div className='structured-data skills-display'>
      <div className='structured-header'>
        <h4>🛠️ Required Skills</h4>
      </div>
      <div className='skills-tags'>
        {skills.map((skill, index) => (
          <span key={index} className='skill-tag'>
            {skill}
          </span>
        ))}
      </div>
    </div>
  );
};

/**
 * Display budget information
 */
const BudgetDisplay = ({ budget }) => {
  return (
    <div className='structured-data budget-display'>
      <div className='structured-header'>
        <h4>💰 Budget</h4>
      </div>
      <div className='budget-info'>
        <div className='budget-amount'>
          ${budget.value?.toLocaleString() || 0}
        </div>
        {budget.confidence && (
          <div className='budget-confidence'>
            Confidence: {(budget.confidence * 100).toFixed(0)}%
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Display interactive buttons
 */
const ButtonsDisplay = ({ buttons, onButtonClick }) => {
  return (
    <div className='buttons-display'>
      {buttons.map((button, index) => (
        <button
          key={index}
          className={`action-button ${button.variant || 'primary'}`}
          onClick={() => onButtonClick && onButtonClick(button)}
          disabled={button.disabled}
        >
          {button.icon && <span className='button-icon'>{button.icon}</span>}
          <span className='button-text'>{button.label}</span>
        </button>
      ))}
    </div>
  );
};

/**
 * Display content with interactive template blanks
 */
const TemplateContent = ({ content, onTemplateClick }) => {
  // Split content by lines and process each line
  const lines = content.split('\n');

  return (
    <div className='template-content'>
      {lines.map((line, lineIndex) => {
        // Check if line contains template blanks
        if (line.includes('_____')) {
          // Split by _____ and create clickable blanks
          const parts = line.split('_____');
          return (
            <div key={lineIndex} className='template-line'>
              {parts.map((part, partIndex) => (
                <React.Fragment key={partIndex}>
                  {part}
                  {partIndex < parts.length - 1 && (
                    <span
                      className='template-blank'
                      onClick={() => onTemplateClick(lineIndex, partIndex)}
                      role='button'
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onTemplateClick(lineIndex, partIndex);
                        }
                      }}
                    >
                      _____
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          );
        }
        // Regular line without blanks
        return (
          <div key={lineIndex} className='template-line'>
            {line}
          </div>
        );
      })}
    </div>
  );
};

export default MessageRenderer;
